import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Collapse, Fade, makeStyles } from '@material-ui/core';
import ActionButton from '../../actionButton';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import InputField from '../../inputField';
import ColorPicker from '../../colorPicker';
import SelectField from '../../selectField';
import CustomSwitch from '../../customSwitch';
import CustomDatePicker from '../../customDatePicker';
import CustomCheckbox from '../../customCheckbox';
import Tooltip from '../../tooltip';
import { ReactComponent as ErrorIcon } from '../../../../assets/icons/warning-triangle.svg';
import {
  ACCRUAL_OPTION_KEYS,
  ABSENCE_DEFAULT_REASONS,
  ABSENCE_FIELDS,
  ABSENCE_FIELD_NAMES,
  DEFAULT_NUMBER_OF_DAYS_COUNT,
} from '../../../../constants/absence';
import { SINGLE_DAY_SAVE_FORMAT } from '../../../../constants/date';
import { DEFAULT_FADE_IN, DEFAULT_FADE_OUT } from '../../../../constants/fade';
import { ACTION_BUTTON_TYPES } from '../../actionButton/config';
import { formatDate, addYearsToDate } from '../../../../utility/dateUtils';
import { isArray, isArrayEmpty } from '../../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    marginBottom: spacing(2),
  },
  info: {
    boxSizing: 'border-box',
    backgroundColor: primary.bluish9,
    borderRadius: 4,
    cursor: 'pointer',
    display: 'grid',
    gridTemplateColumns: '22px 1fr 22px 30px',
    alignItems: 'center',
    padding: spacing(2),
    gap: 6,
    transition: 'background-color .3s ease',
    '&:hover': {
      backgroundColor: primary.bluish8,
    },
  },
  infoDeletable: {
    gridTemplateColumns: '22px 1fr 22px 30px 30px',
  },
  absenceTitleWrapper: {
    display: 'flex',
    minWidth: 0,
  },
  absenceTitle: {
    cursor: 'pointer',
  },
  dragButton: {
    position: 'relative',
    width: 22,
    height: 22,
    flexShrink: 0,
    zIndex: 11,
    '& button': {
      width: 22,
      height: 22,
    },
  },
  dragIcon: {
    width: 8,
    height: 14,
  },
  dragTooltipLabel: {
    fontFamily: 'ProximaNova-Regular',
  },
  expandIcon: {
    transform: 'rotate(0deg)',
    transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
  },
  expandIconOpened: {
    transform: 'rotate(180deg)',
  },
  form: {
    borderBottom: `1px solid ${primary.bluish5}`,
    marginTop: spacing(3),
    width: '100%',
  },
  field: {
    marginBottom: spacing(6),
  },
  error: {
    marginBottom: spacing(1),
  },
  twoColumnFieldWraper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 24,
    alignItems: 'center',
  },
  colorPicker: {
    width: 'fit-content',
  },
  availabledays: {
    width: 75,
  },
  checkbox: {
    width: '100%',
  },
}));

const AbsenceField = ({
  translations,
  index,
  initialAbsence = null,
  absence,
  absenceIcons,
  accrualOptions,
  isExpanded,
  errors = null,
  dragRef = () => {},
  dragProps = {},
  onToggleAbsence,
  onDelete,
  onChange,
}) => {
  const classes = useStyles();

  const [
    nameField,
    iconsField,
    colorField,
    hasAvailableDaysField,
    availableDaysField,
    accrualField,
    hasRolloverField,
    rolloverDateField,
    hasExtensionsField,
    hasTemplateField,
    hasReasonsField,
  ] = ABSENCE_FIELDS;
  const {
    NAME,
    COLOR,
    ICON,
    NUMBER_OF_DAYS,
    HAS_DAYS_ENABLED,
    ACCRUAL,
    HAS_ROLLOVER,
    ROLLOVER_DATE,
    HAS_EXTENSIONS,
    EXTENSIONS,
    HAS_TEMPLATE,
    HAS_REASONS,
    USER_COUNT,
  } = ABSENCE_FIELD_NAMES;
  const { id, tempId } = absence;

  const isHasExtensionsDisabled =
    absence[HAS_DAYS_ENABLED] &&
    absence[HAS_EXTENSIONS] &&
    !isArrayEmpty(absence[EXTENSIONS]) &&
    absence[EXTENSIONS].some(extension => extension[USER_COUNT] > 0);

  const handleExpand = useCallback(() => {
    if (isExpanded) {
      onToggleAbsence(0);
    } else {
      onToggleAbsence(id || tempId);
    }
  }, [id, tempId, isExpanded, onToggleAbsence]);

  const handleDelete = useCallback(
    () => onDelete(absence),
    [absence, onDelete]
  );

  const handleChange = useCallback(
    field => value => {
      onChange(index, { ...absence, [field.name]: value });
    },
    [index, absence, onChange]
  );

  const handleTextChange = useCallback(
    field => event => {
      handleChange(field)(event.target.value);
    },
    [handleChange]
  );

  const handleDateChange = useCallback(
    field => value => {
      const formatedValue = field.valueFormat
        ? formatDate(value, field.valueFormat)
        : value;

      handleChange(field)(formatedValue);
    },
    [handleChange]
  );

  const handleHasAvailableDaysChange = useCallback(
    field => value => {
      onChange(index, {
        ...absence,
        [field.name]: value,
        ...(value
          ? {
              [ABSENCE_FIELD_NAMES.NUMBER_OF_DAYS]:
                initialAbsence?.[ABSENCE_FIELD_NAMES.NUMBER_OF_DAYS] ||
                DEFAULT_NUMBER_OF_DAYS_COUNT,
              [ABSENCE_FIELD_NAMES.ACCRUAL]:
                initialAbsence?.[ABSENCE_FIELD_NAMES.ACCRUAL] ||
                ACCRUAL_OPTION_KEYS.YEARLY,
              [ABSENCE_FIELD_NAMES.HAS_ROLLOVER]:
                initialAbsence?.[ABSENCE_FIELD_NAMES.HAS_ROLLOVER] || false,
              [ABSENCE_FIELD_NAMES.ROLLOVER_DATE]:
                initialAbsence?.[ABSENCE_FIELD_NAMES.ROLLOVER_DATE] ||
                formatDate(addYearsToDate(), SINGLE_DAY_SAVE_FORMAT),
              [ABSENCE_FIELD_NAMES.HAS_EXTENSIONS]:
                initialAbsence?.[ABSENCE_FIELD_NAMES.HAS_EXTENSIONS] || false,
            }
          : {
              [ABSENCE_FIELD_NAMES.NUMBER_OF_DAYS]: 0,
              [ABSENCE_FIELD_NAMES.ACCRUAL]: null,
              [ABSENCE_FIELD_NAMES.HAS_ROLLOVER]: false,
              [ABSENCE_FIELD_NAMES.HAS_EXTENSIONS]: false,
              [ABSENCE_FIELD_NAMES.ROLLOVER_DATE]: null,
            }),
      });
    },
    [initialAbsence, absence, index, onChange]
  );

  const handleHasRolloverChange = useCallback(
    field => value => {
      onChange(index, {
        ...absence,
        [field.name]: value,
        ...(value
          ? {
              [ABSENCE_FIELD_NAMES.ROLLOVER_DATE]:
                initialAbsence?.[ABSENCE_FIELD_NAMES.ROLLOVER_DATE] ||
                formatDate(addYearsToDate(), SINGLE_DAY_SAVE_FORMAT),
            }
          : { [ABSENCE_FIELD_NAMES.ROLLOVER_DATE]: null }),
      });
    },
    [initialAbsence, absence, index, onChange]
  );

  const handleHasTemplateChange = useCallback(
    field => value => {
      onChange(index, {
        ...absence,
        [field.name]: value,
        ...(value
          ? {
              [ABSENCE_FIELD_NAMES.TEMPLATE]:
                initialAbsence?.[ABSENCE_FIELD_NAMES.TEMPLATE] ?? '',
            }
          : { [ABSENCE_FIELD_NAMES.TEMPLATE]: '' }),
      });
    },
    [initialAbsence, absence, index, onChange]
  );

  const handleHasReasonsChange = useCallback(
    field => value => {
      onChange(index, {
        ...absence,
        [field.name]: value,
        ...(value
          ? {
              [ABSENCE_FIELD_NAMES.REASONS]:
                isArray(initialAbsence?.[ABSENCE_FIELD_NAMES.REASONS]) &&
                !isArrayEmpty(initialAbsence?.[ABSENCE_FIELD_NAMES.REASONS])
                  ? initialAbsence?.[ABSENCE_FIELD_NAMES.REASONS]
                  : ABSENCE_DEFAULT_REASONS,
            }
          : { [ABSENCE_FIELD_NAMES.REASONS]: [] }),
      });
    },
    [initialAbsence, absence, index, onChange]
  );

  return (
    <div
      className={classes.root}
      ref={dragRef}
      {...(dragProps ? { ...dragProps.draggableProps } : {})}
    >
      <div
        className={classNames(classes.info, {
          [classes.infoDeletable]: tempId,
        })}
        onClick={handleExpand}
      >
        <ActionButton
          className={classes.dragButton}
          iconClass={classes.dragIcon}
          tooltipLabelClass={classes.dragTooltipLabel}
          type={ACTION_BUTTON_TYPES.DRAG}
          tooltipText={translations.dragTooltip}
          {...(dragProps ? { ...dragProps.dragHandleProps } : {})}
        />
        <div className={classes.absenceTitleWrapper}>
          <div className={classes.absenceTitleWrapper}>
            <TextBoxWithTooltip
              className={classes.absenceTitle}
              variant="subtitle2"
              text={absence[NAME]}
            />
          </div>
        </div>
        <Fade in={!!errors}>
          <div>
            <Tooltip
              text={translations.absenceErrorsTooltip}
              icon={ErrorIcon}
            />
          </div>
        </Fade>
        {tempId && (
          <ActionButton
            iconClass={classes.deleteIcon}
            type={ACTION_BUTTON_TYPES.DELETE_FILL}
            onClickHandler={handleDelete}
          />
        )}
        <ActionButton
          type={ACTION_BUTTON_TYPES.ARROW}
          iconClass={classNames(classes.expandIcon, {
            [classes.expandIconOpened]: isExpanded,
          })}
        />
      </div>
      <Collapse in={isExpanded} unmountOnExit>
        <div className={classes.form}>
          <div
            className={classNames(classes.field, {
              [classes.error]: !!errors?.[NAME],
            })}
          >
            <InputField
              id={nameField.name}
              name={nameField.name}
              type={nameField.type}
              label={translations.formLabels[nameField.name].label}
              placeholder={translations.formLabels[nameField.name].placeholder}
              required={nameField.required}
              value={absence[NAME]}
              error={!!errors?.[NAME]}
              errorMessage={translations.validationMessages?.[errors?.[NAME]]}
              onChange={handleTextChange(nameField)}
              fullWidth
            />
          </div>
          <div className={classes.twoColumnFieldWraper}>
            <div className={classes.field}>
              <SelectField
                id={iconsField.name}
                label={translations.formLabels[iconsField.name].label}
                options={absenceIcons}
                parser={iconsField.parser}
                value={absence[ICON]}
                onChange={handleChange(iconsField)}
                isIcon
                isFullWidth
                isSearchDisabled
              />
            </div>
            <div className={classes.field}>
              <ColorPicker
                className={classes.colorPicker}
                label={translations.formLabels[colorField.name].label}
                tooltipText={translations.formLabels[colorField.name].tooltip}
                selectedColor={absence[COLOR]}
                collorPalette={colorField.collorPalette}
                shouldDisableCustomColorPick={
                  colorField.shouldDisableCustomColorPick
                }
                onColorPick={handleChange(colorField)}
              />
            </div>
          </div>
          <div className={classes.field}>
            <CustomSwitch
              label={
                translations.formLabels[
                  hasAvailableDaysField.translationKey ||
                    hasAvailableDaysField.name
                ]?.label
              }
              tooltipInfo={
                translations.formLabels[
                  hasAvailableDaysField.translationKey ||
                    hasAvailableDaysField.name
                ]?.tooltipInfo
              }
              disabledTooltipText={
                translations.formLabels[
                  hasAvailableDaysField.translationKey ||
                    hasAvailableDaysField.name
                ]?.disabledTooltip
              }
              isDisabled={isHasExtensionsDisabled}
              isHighlightedLabel={hasAvailableDaysField.isHighlightedLabel}
              isChecked={absence[HAS_DAYS_ENABLED]}
              onChange={handleHasAvailableDaysChange(hasAvailableDaysField)}
            />
          </div>
          <Fade
            in={availableDaysField.shouldRender(absence)}
            timeout={{
              enter: DEFAULT_FADE_IN,
              exit: availableDaysField.shouldFadeOutInstant
                ? 0
                : DEFAULT_FADE_OUT,
            }}
            unmountOnExit
          >
            <div
              className={classNames(classes.field, {
                [classes.error]: !!errors?.[NUMBER_OF_DAYS],
              })}
            >
              <InputField
                id={availableDaysField.name}
                name={availableDaysField.name}
                type={availableDaysField.inputType || availableDaysField.type}
                customRootClass={classes.availabledays}
                required={availableDaysField.required}
                value={absence[NUMBER_OF_DAYS]}
                error={!!errors?.[NUMBER_OF_DAYS]}
                errorMessage={
                  translations.validationMessages?.[errors?.[NUMBER_OF_DAYS]]
                }
                onChange={handleTextChange(availableDaysField)}
                fullWidth
              />
            </div>
          </Fade>
          <Fade
            in={accrualField.shouldRender(absence)}
            timeout={{
              enter: DEFAULT_FADE_IN,
              exit: accrualField.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
            }}
            unmountOnExit
          >
            <div className={classes.field}>
              <SelectField
                id={accrualField.name}
                label={
                  translations.formLabels[
                    accrualField.translationKey || accrualField.name
                  ]?.label
                }
                options={accrualOptions}
                parser={accrualField.parser}
                value={absence[ACCRUAL]}
                onChange={handleChange(accrualField)}
                isFullWidth
                isSearchDisabled
              />
            </div>
          </Fade>
          <Fade
            in={hasRolloverField.shouldRender(absence)}
            timeout={{
              enter: DEFAULT_FADE_IN,
              exit: hasRolloverField.shouldFadeOutInstant
                ? 0
                : DEFAULT_FADE_OUT,
            }}
            unmountOnExit
          >
            <div className={classes.field}>
              <CustomSwitch
                label={
                  translations.formLabels[
                    hasRolloverField.translationKey || hasRolloverField.name
                  ]?.label
                }
                tooltipInfo={
                  translations.formLabels[
                    hasRolloverField.translationKey || hasRolloverField.name
                  ]?.tooltipInfo
                }
                isHighlightedLabel={hasRolloverField.isHighlightedLabel}
                isChecked={absence[HAS_ROLLOVER]}
                onChange={handleHasRolloverChange(hasRolloverField)}
              />
            </div>
          </Fade>
          <Fade
            in={rolloverDateField.shouldRender(absence)}
            timeout={{
              enter: DEFAULT_FADE_IN,
              exit: rolloverDateField.shouldFadeOutInstant
                ? 0
                : DEFAULT_FADE_OUT,
            }}
            unmountOnExit
          >
            <div className={classes.field}>
              <CustomDatePicker
                name={rolloverDateField.name}
                label={
                  translations.formLabels[
                    rolloverDateField.translationKey || rolloverDateField.name
                  ]?.label
                }
                tooltipInfo={
                  translations.formLabels[
                    rolloverDateField.translationKey || rolloverDateField.name
                  ]?.tooltipInfo
                }
                value={absence[ROLLOVER_DATE] || null}
                format={rolloverDateField.selectedDateFormat}
                required={rolloverDateField.required}
                shouldDisableDate={rolloverDateField.shouldDisableDate}
                minDate={
                  rolloverDateField.getMinDate
                    ? rolloverDateField.getMinDate()
                    : undefined
                }
                maxDate={
                  rolloverDateField.getMaxDate
                    ? rolloverDateField.getMaxDate()
                    : undefined
                }
                onChange={handleDateChange(rolloverDateField)}
                fullWidth
                autoOk
                isIconOnStart
              />
            </div>
          </Fade>
          <Fade
            in={hasExtensionsField.shouldRender(absence)}
            timeout={{
              enter: DEFAULT_FADE_IN,
              exit: hasExtensionsField.shouldFadeOutInstant
                ? 0
                : DEFAULT_FADE_OUT,
            }}
            unmountOnExit
          >
            <div className={classes.field}>
              <CustomCheckbox
                className={classes.checkbox}
                label={
                  translations.formLabels[
                    hasExtensionsField.translationKey || hasExtensionsField.name
                  ].label
                }
                labelText={
                  translations.formLabels[
                    hasExtensionsField.translationKey || hasExtensionsField.name
                  ].labelText
                }
                labelHelp={
                  translations.formLabels[
                    hasExtensionsField.translationKey || hasExtensionsField.name
                  ].labelHelp
                }
                disabledTooltipText={
                  translations.formLabels[
                    hasExtensionsField.translationKey || hasExtensionsField.name
                  ].disabledTooltip
                }
                isChecked={absence[HAS_EXTENSIONS]}
                isControlled={hasExtensionsField.isControlled}
                disabled={isHasExtensionsDisabled}
                smallText={hasExtensionsField.smallText}
                onChange={handleChange(hasExtensionsField)}
              />
            </div>
          </Fade>
          <div className={classes.field}>
            <CustomCheckbox
              className={classes.checkbox}
              label={
                translations.formLabels[
                  hasTemplateField.translationKey || hasTemplateField.name
                ].label
              }
              labelText={
                translations.formLabels[
                  hasTemplateField.translationKey || hasTemplateField.name
                ].labelText
              }
              labelHelp={
                translations.formLabels[
                  hasTemplateField.translationKey || hasTemplateField.name
                ].labelHelp
              }
              isChecked={absence[HAS_TEMPLATE]}
              isControlled={hasTemplateField.isControlled}
              smallText={hasTemplateField.smallText}
              onChange={handleHasTemplateChange(hasTemplateField)}
            />
          </div>
          <div className={classes.field}>
            <CustomCheckbox
              className={classes.checkbox}
              label={
                translations.formLabels[
                  hasReasonsField.translationKey || hasReasonsField.name
                ].label
              }
              labelText={
                translations.formLabels[
                  hasReasonsField.translationKey || hasReasonsField.name
                ].labelText
              }
              labelHelp={
                translations.formLabels[
                  hasReasonsField.translationKey || hasReasonsField.name
                ].labelHelp
              }
              isChecked={absence[HAS_REASONS]}
              isControlled={hasReasonsField.isControlled}
              smallText={hasReasonsField.smallText}
              onChange={handleHasReasonsChange(hasReasonsField)}
            />
          </div>
        </div>
      </Collapse>
    </div>
  );
};

AbsenceField.propTypes = {
  translations: PropTypes.object.isRequired,
  initialAbsence: PropTypes.object,
  absence: PropTypes.object.isRequired,
  absenceIcons: PropTypes.arrayOf(PropTypes.object).isRequired,
  accrualOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  dragProps: PropTypes.object,
  index: PropTypes.number.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  errors: PropTypes.object,
  dragRef: PropTypes.func,
  onToggleAbsence: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default AbsenceField;
